import { useCallback, useMemo, useState } from 'react';
import { v4 as uuid } from 'uuid';

export type WebWorkerInput<T> = { requestId: string; input: T };
export type WebWorkerOutput<T> = {
  requestId: string;
  output?: T;
  error?: unknown;
};
type OutputHandler<O> = (event: MessageEvent<WebWorkerOutput<O>>) => void;
type Callback<O> = ({ output, error }: { output?: O; error?: unknown }) => void;

export class WebWorker<I, O> {
  worker: Worker;
  callbacks = new Map<string, Callback<O>>();

  constructor(creator: () => Worker) {
    this.worker = creator();
    this.worker.addEventListener('error', (event) => {
      console.error(event.error);
    });
    this.worker.addEventListener('messageerror', (event) => {
      console.error(event.data);
    });
    this.#addMessageListener(({ data: { requestId, output, error } }) => {
      const callback = this.callbacks.get(requestId);
      if (!callback) {
        throw new Error(`no callback for request ${requestId}`);
      }
      if (error) {
        callback({ error });
      } else if (!output) {
        callback({ error: new Error('Missing output') });
      } else {
        callback({ output });
      }
      this.callbacks.delete(requestId);
    });
  }

  #postMessage = (input: WebWorkerInput<I>) => this.worker.postMessage(input);

  #addMessageListener = (handler: OutputHandler<O>) =>
    this.worker.addEventListener('message', handler);

  // #removeMessageListener = (handler: Handler<O>) =>
  //   this.worker.removeEventListener('message', handler);

  call(input: I, callback: Callback<O>) {
    const requestId = uuid();
    this.callbacks.set(requestId, callback);
    this.#postMessage({ requestId, input });
  }

  terminate() {
    this.callbacks.clear();
    this.worker.terminate();
  }
}

export function useWebWorker<I, O>(webWorker: WebWorker<I, O>) {
  const [loading, setLoading] = useState(false);

  const call = useCallback(
    async (message: I) => {
      try {
        setLoading(true);
        const data = await new Promise<O>((ok, reject) => {
          webWorker.call(message, ({ error, output }) => {
            if (error) {
              reject(error);
            } else if (!output) {
              reject(new Error('Missing output'));
            } else {
              ok(output);
            }
          });
        });
        return data;
      } finally {
        setLoading(false);
      }
    },
    [webWorker]
  );

  return useMemo(() => ({ loading, call }), [loading, call]);
}
